import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, MessageSquare, Send } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_customer: { label: "Awaiting Your Reply", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ subject: "", category: "General", order_number: "", message: "" });
  const [reply, setReply] = useState("");
  const load = () => api.get("/tickets").then((r) => setTickets(r.data.tickets));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/tickets", form);
      toast.success("Ticket created — we'll reply shortly");
      setOpen(false); setForm({ subject: "", category: "General", order_number: "", message: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openTicket = async (t) => {
    setActive(t);
    const { data } = await api.get(`/tickets/${t.id}`);
    setMessages(data.messages);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setReply("");
      const { data } = await api.get(`/tickets/${active.id}`);
      setMessages(data.messages);
      load();
    } catch { toast.error("Reply failed"); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="section-label">Help &amp; Support</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Support Tickets</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="new-ticket-btn"><Plus className="w-4 h-4 mr-1" /> New ticket</Button>
      </div>

      {tickets.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center" data-testid="tickets-empty">
          <LifeBuoy className="w-10 h-10 mx-auto text-slate-400" />
          <div className="mt-3 font-display font-semibold text-lg text-[#0A192F]">No tickets yet</div>
          <div className="text-sm text-slate-500 mt-1">Open a ticket and our team replies within 1 business day.</div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="p-3 text-left">Ticket ID</th><th className="p-3 text-left">Subject</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Updated</th><th /></tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ticket-row-${t.ticket_id}`}>
                  <td className="p-3 font-mono text-xs">{t.ticket_id}</td>
                  <td className="p-3">{t.subject}</td>
                  <td className="p-3">{t.category}</td>
                  <td className="p-3"><span className={`inline-block text-xs px-2 py-1 rounded border ${STATUS_META[t.status]?.cls || "bg-slate-50"}`}>{STATUS_META[t.status]?.label || t.status}</span></td>
                  <td className="p-3 text-xs text-slate-500">{new Date(t.updated_at).toLocaleString()}</td>
                  <td className="p-3"><Button size="sm" variant="outline" onClick={() => openTicket(t)} data-testid={`ticket-open-${t.ticket_id}`}><MessageSquare className="w-3 h-3 mr-1" /> View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New ticket dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ticket-form-dialog">
          <DialogHeader><DialogTitle>Create a support ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-md h-10 px-2" data-testid="ticket-category">
                  <option>General</option><option>Order Issue</option><option>Payment</option><option>Refund</option><option>Documents</option><option>Feature Request</option><option>Other</option>
                </select></div>
              <div><Label>Order Number (optional)</Label><Input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="W2E-1010" data-testid="ticket-order" /></div>
            </div>
            <div><Label>Message</Label><Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={!form.subject || !form.message} data-testid="ticket-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket conversation dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="ticket-conversation">
          <DialogHeader><DialogTitle>{active?.ticket_id} · {active?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {messages.map((m) => (
              <div key={m.id} className={`p-3 rounded-lg border ${m.author_role === "admin" ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-200"}`}>
                <div className="text-xs text-slate-500 mb-1">{m.author_name} · <span className="capitalize">{m.author_role}</span> · {new Date(m.created_at).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap text-slate-800">{m.message}</div>
              </div>
            ))}
          </div>
          {active && active.status !== "closed" && (
            <div className="mt-3 flex gap-2">
              <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="ticket-reply-input" />
              <Button onClick={sendReply} disabled={!reply.trim()} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="ticket-reply-send"><Send className="w-4 h-4" /></Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
